/**
 * ErrorBoundary Component
 * 
 * A React error boundary component that catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of crashing the application.
 * 
 * Features:
 * - Catches errors in child components
 * - Provides a user-friendly error message
 * - Offers recovery options (refresh page or go to homepage)
 * - Logs errors to console for debugging
 */

'use client';

import { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Props interface for the ErrorBoundary component
 * @interface Props
 * @property {ReactNode} children - The child components to be wrapped by the error boundary
 */
interface Props {
	children: ReactNode;
}

/**
 * State interface for the ErrorBoundary component
 * @interface State
 * @property {boolean} hasError - Indicates whether an error has been caught
 * @property {Error | undefined} error - The caught error object, if any
 */
interface State {
	hasError: boolean;
	error?: Error;
}

/**
 * ErrorBoundary class component that implements React's error boundary pattern
 * @class ErrorBoundary
 * @extends {Component<Props, State>}
 */
export class ErrorBoundary extends Component<Props, State> {
	/**
	 * Initializes the component state
	 */
	public state: State = {
		hasError: false,
	};

	/**
	 * Updates state when an error is caught
	 * @static
	 * @param {Error} error - The caught error
	 * @returns {State} The new state with error information
	 */
	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	/**
	 * Handles error logging when an error is caught
	 * @param {Error} error - The caught error
	 * @param {ErrorInfo} errorInfo - Additional error information
	 */
	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('Uncaught error:', error, errorInfo);
	}

	/**
	 * Resets the error state and reloads the page
	 * @memberof ErrorBoundary
	 */
	handleReset = () => {
		this.setState({ hasError: false, error: undefined });
		window.location.reload();
	};

	/**
	 * Renders the component
	 * @returns {ReactNode} The error UI or children components
	 */
	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-background">
					<div className="max-w-md w-full p-6 bg-card rounded-lg shadow-lg">
						<h2 className="text-2xl font-bold text-foreground mb-4">Oops! Something went wrong</h2>
						<p className="text-muted-foreground mb-4">
							We apologize for the inconvenience. Please try refreshing the page or contact support
							if the problem persists.
						</p>
						<div className="space-y-4">
							<Button onClick={this.handleReset} className="w-full">
								Refresh Page
							</Button>
							<Button
								variant="outline"
								className="w-full"
								onClick={() => (window.location.href = '/')}
							>
								Go to Homepage
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
